/**
 * Estado de Cuenta del Consignante.
 *
 * El espejo de ares8535 (estado de cuenta de suplidor) para consignaciones.
 * Existe porque el estado de cuenta normal ve el conduce por lo vendido y
 * ya está, sin poder decir cuánta mercancía del consignante queda en piso.
 *
 * Las tres columnas que importan: EN PISO (suyo, se le puede devolver),
 * POR PAGAR (vendido y no pagado: la deuda de verdad) y PAGADO.
 */
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx, StatusPill } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { estadoConsignacion } from "lib/consignaciones";
import { imprimirListadoGrande } from "lib/impresionhtml";

const hoyISO = () => new Date().toISOString().slice(0, 10);

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseN = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const moneyCol = (field, headerName, width = 120) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Seleccione un consignante y presione Procesar
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 130,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

export default function EstadoCuentaConsignante() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [consignantes, setConsignantes] = useState([]);
  const [consignante, setConsignante] = useState(null);
  const [fechacorte, setFechacorte] = useState(hoyISO());
  const [facturas, setFacturas] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { ok, data } = await post("/api/cxp_suplidores", {});
        if (ok) setConsignantes(data.suplidores || []);
      } catch (err) {
        setConsignantes([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    if (!consignante) {
      showAlert({ message: "Seleccione el consignante", color: "error" });
      return;
    }
    setLoading(true);
    try {
      const { ok, data } = await post("/api/consignaciones_estadocuenta", {
        suplidor: consignante.id,
        fechacorte,
      });
      if (ok) {
        setFacturas(data.facturas || []);
        setTotales(data.totales || null);
      } else {
        showAlert({
          message: data.Error || "No se pudo generar el estado de cuenta",
          color: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const columnas = [
    { field: "nfactura", headerName: "Fact#", width: 95 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "motores", headerName: "Motores", width: 90, align: "right", headerAlign: "right" },
    { field: "enpiso", headerName: "En piso", width: 90, align: "right", headerAlign: "right" },
    { field: "vendidos", headerName: "Vendidos", width: 95, align: "right", headerAlign: "right" },
    { field: "devueltos", headerName: "Devueltos", width: 95, align: "right", headerAlign: "right" },
    moneyCol("costopiso", "Costo en piso", 135),
    moneyCol("costovendido", "Costo vendido", 135),
    moneyCol("vplicado", "Pagado", 120),
    moneyCol("porpagar", "Por pagar", 130),
    {
      field: "estado",
      headerName: "Estado",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        // No decía "Saldada" sino "Al día", pero daba ese estado por bueno
        // sin mirar el piso: una consignación con motores sin vender no está
        // cerrada aunque hoy no se deba nada.
        const { label, variant } = estadoConsignacion(params.row, { femenino: true });
        return <StatusPill label={label} variant={variant} />;
      },
    },
  ];

  const subtitulo = consignante
    ? `${consignante.descrip} — al ${fechaDMY(fechacorte)}`
    : `Al ${fechaDMY(fechacorte)}`;

  const cabeceras = [
    "Fact#", "Fecha", "Motores", "En piso", "Vendidos", "Devueltos",
    "Costo en piso", "Costo vendido", "Pagado", "Por pagar",
  ];

  const filasPlanas = () =>
    facturas.map((f) => [
      f.nfactura, f.fecha, f.motores, f.enpiso, f.vendidos, f.devueltos,
      fmtMoney(f.costopiso), fmtMoney(f.costovendido),
      fmtMoney(f.vplicado), fmtMoney(f.porpagar),
    ]);

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Estado de Cuenta del Consignante"],
      [subtitulo],
      [],
      cabeceras,
      ...filasPlanas(),
      [],
      ["", "", "", totales?.enpiso ?? 0, totales?.vendidos ?? 0, totales?.devueltos ?? 0,
        fmtMoney(totales?.costopiso), fmtMoney(totales?.costovendido),
        fmtMoney(totales?.vplicado), fmtMoney(totales?.porpagar)],
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Consignante");
    XLSX.writeFile(libro, `estadocuenta_consignante_${hoyISO()}.xlsx`);
  };

  const imprimir = () =>
    imprimirListadoGrande({
      titulo: "Estado de Cuenta del Consignante",
      subtitulo,
      cabeceras,
      filas: filasPlanas(),
      totales: [
        "", "", "", String(totales?.enpiso ?? 0), String(totales?.vendidos ?? 0),
        String(totales?.devueltos ?? 0), fmtMoney(totales?.costopiso),
        fmtMoney(totales?.costovendido), fmtMoney(totales?.vplicado),
        fmtMoney(totales?.porpagar),
      ],
      numericas: [2, 3, 4, 5, 6, 7, 8, 9],
    });

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Estado de Cuenta del Consignante"
        subtitle="Lo que está en piso es del consignante; solo lo vendido es deuda"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={5} md={4}>
              <Autocomplete
                options={consignantes}
                value={consignante}
                onChange={(_e, v) => setConsignante(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Consignante" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de corte"
                InputLabelProps={{ shrink: true }}
                value={fechacorte}
                onChange={(e) => setFechacorte(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            {facturas.length > 0 && (
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1.2} justifyContent={{ md: "flex-end" }}>
                  <Button
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={exportar}
                    sx={btnOutline(FP.color.navy)}
                  >
                    Excel
                  </Button>
                  <Button
                    startIcon={<PrintOutlinedIcon />}
                    onClick={imprimir}
                    sx={btnOutline(FP.color.navy)}
                  >
                    Imprimir
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>

          {totales && (
            <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
              <ResumenChip
                label="EN PISO (NO ES DEUDA)"
                value={`RD$ ${fmtMoney(totales.costopiso)}`}
                color={FP.color.textSecondary}
              />
              <ResumenChip
                label="POR PAGAR"
                value={`RD$ ${fmtMoney(totales.porpagar)}`}
                color={FP.color.danger}
              />
              <ResumenChip
                label="PAGADO"
                value={`RD$ ${fmtMoney(totales.vplicado)}`}
                color="#2E7D32"
              />
              <ResumenChip label="MOTORES EN PISO" value={String(totales.enpiso)} />
              <ResumenChip label="VENDIDOS" value={String(totales.vendidos)} />
            </Stack>
          )}

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={facturas}
              columns={columnas}
              getRowId={(r) => `${r.nfactura}-${r.suplidor}`}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
